<template>
  <v-tab-item
    class="members'"
    data-qa="item-tab-members">
    <v-skeleton-loader
      v-if="load_members"
      :boilerplate="false"
      class="mt-4"
      :tile="false"
      type=" table-thead, table-tbody, table-tbody"
      width="100%"/>
    <EmptyBlock
      v-if="!tab || !tab.count"
      custom-class="mt-0 pa-10 no-members"
      data-qa="members-noData"
      :text="$t('Tenders.NoData')">
      <template #append>
        <sButton
          v-if="in_creating"
          class="mt-5 block"
          color="var(--color-error)"
          @click="returnToStep('Members')">
          {{ $t('Tenders.InviteAnother') }}
        </sButton>
      </template>
    </EmptyBlock>
    <div
      v-else
      class="members">
      <Groups
        ref="groups"
        can_expand
        class="members-groups"
        :filter-empty-by="filterEmptyBy"
        filter_empty
        :group-link-func="(group, n, groups) => n + '' + groups.length + '' + group.count + (group.items ? group.items.length : group.subscribersCount) || ''"
        :groups.sync="members_groups"
        items-key="items"
        :with_title="(members_groups && members_groups.length > 1) || members_groups.some(el => el.inactive)"
        @toggle-select-all="(args) => toggleSelectAll(group, args )"
        @update:expanded="updExpanded">
        <template
          #score="{ group }">
          <div
            class="mx-3 secondary__green_not_active--text h7-text-medium"
            data-qa="members-summ">
            <template v-if="group.countGroup">
              <sTooltip
                :border="false"
                type="white">
                <div>
                  ( {{ group.countGroup.total }} )
                </div>
                <template #content>
                  <div
                    v-for="(groupItem, groupIndex) in group.countGroup.group"
                    :key="groupIndex">
                    <b>{{ groupItem.title }}</b> - <span>{{ groupItem.count }}</span>
                  </div>
                </template>
              </sTooltip>
            </template>
            <template v-else>
              ( {{ group.items.length }} )
            </template>
          </div>
        </template>
        <template #content="{ group }">
          <div
            class="main__green px-0 ">
            <div
              v-if="(bidding_type && (bidding_type.isPublic || bidding_type.showSubscribedUsers)) && group.subscribersCount"
              class="all-subscribers-block rounded-lg main__light_green mb-4">
              <SubscribedToCategory
                :subscribed-to-category="subscribedToCategory"
                :tender="tender"/>
            </div>
            <v-data-table
              v-if="group.items && group.items.length"
              v-model="selected_members"
              calculate-widths
              class="elevation-0 members-table tender-table main__light_green"
              :class="{ 'expanded_members': group.active } "
              :custom-sort="(...args) => customSortSlice(group, args)"
              disable-pagination
              :expanded="group.active ? expanded_members : []"
              fixed-header
              :headers="members_headers(group.winners)"
              hide-default-footer
              item-key="id"
              :items="group.items"
              :loading-text="$t('Tenders.Loading')"
              :no-data-text="$t('Tenders.NoData')"
              :no-results-text="$t('Tenders.NoData')"
              :show-select="group.winners || group.bidders"
              single-expand
              @click:row="expandRow">
              <template #[`item.organizationTitle`]="{ item }">
                <div>
                  <VerificationStatus
                    v-if="item.verificationStatus == 'VERIFIED'"
                    class="mr-1"
                    :status="item.verificationDecision"/>
                  {{ item.organizationTitle || item.organizationName || $t('Tenders.InvitedDraft') }}
                </div>
                <div
                  v-if="item.organizationId"
                  class="h7-text secondary__green_not_active--text">
                  {{ $t('Tenders.Rating') }}: {{ item.supplierRatings || '–' }}
                </div>
              </template>
              <template #[`item.organizationUsreou`]="{ item }">
                {{ item.organizationUsreou || item.usreou || '–' }}
              </template>
              <template #[`item.participationCount`]="{ item }">
                <span class="h7-text">{{ item.participationCount || '–' }}</span>
              </template>
              <template #[`item.contract`]="{ item }">
                <template
                  v-if="!contranctProps || !contranctProps.contractLoaded">
                  <v-skeleton-loader
                    :boilerplate="false"
                    :tile="false"
                    type="text"/>
                  <v-skeleton-loader
                    :boilerplate="false"
                    :tile="false"
                    type="chip"/>
                </template>
                <div
                  v-else>
                  <div class="flex mb-2 ml-1">
                    <sIcon
                      class="mr-2 my-auto"
                      :size="12"
                      type="draft"/>
                    <div> {{ item.contractCount ? item.contractCount.all : 0 }}</div>
                  </div>
                  <StatusChip
                    :class="`contract-chip black--text ${item.contractCount && item.contractCount.all ? item.contract[0].status : ''}`"
                    :color="item.contractCount && item.contractCount.all ? $t('Tenders.ContractStatus.' + item.contract[0].status+'.color') : 'var(--color-error)'"
                    data-qa="file-status"
                    :name="item.contractCount && item.contractCount.all ? $t('Tenders.ContractStatus.' + item.contract[0].status+'.label') : $t('Tenders.NotFormed')"/>
                </div>
              </template>
              <template #[`item.email`]="{ item }">
                <div>
                  <div> {{ item.email || '–' }}</div>
                  <ParticipantChip
                    v-if="item.manualAdded"
                    custom_class="mr-2 my-2 "
                    :item="item"
                    manual-added/>
                  <ParticipantChip
                    v-if="!item.organizationId && !item.organizationUsreou && !item.usreou"
                    unregistred/>
                  <ExternalParticipantChip
                    v-if="item.foreignBidderBid"
                    class="mr-2 my-2"/>
                </div>
              </template>
              <template #[`item.userName`]="{ item }">
                <span> {{ item.userName || item.fullName || '–' }}</span>
              </template>
              <template #expanded-item="{ item, headers }">
                <td
                  :colspan="2"
                  style="cursor: default; vertical-align: top;"/>
                <td
                  :colspan="1"
                  style="cursor: default; vertical-align: top;">
                  <KeyValue
                    :caption="$t('Tenders.Address')"
                    data-qa="organization-address"
                    :value="item.organizationAddress"/>
                </td>
                <td
                  :colspan="1"
                  style="cursor: default; vertical-align: top;">
                  <KeyValue
                    :caption="$t('Common.PhoneNumber')"
                    data-qa="phone-number"
                    :value="item.phoneNumber"/>
                </td>
                <td
                  :colspan="headers.length - (tender.tenderStatus.id == 10 ? 3 : 2)"
                  style="cursor: default; vertical-align: top;">
                  <VerificationResultBlock
                    v-if="item.verification && (item.verification.comment || item.verification.decisionType || item.verification.verificationStatus)"
                    :comment="item.verification.comment"
                    data-qa="bidder-info"
                    :decision-type="item.verification.decisionType"
                    :verification-status="item.verification.verificationStatus"/>
                  <template
                    v-if="item.winner && item.contract">
                    <div
                      class="h9-label">
                      {{ $t('Menu.Contracting') }}
                    </div>
                    <ContractDocuments
                      :items="item.contract"/>
                  </template>
                  <template
                    v-if="item.winner && item.approving">
                    <div
                      class="h9-label mt-4">
                      {{ $t('Tenders.AdditionalAgreements') }}
                    </div>
                    <ContractDocuments
                      :items="item.approving"/>
                  </template>
                </td>
              </template>
            </v-data-table>
          </div>
        </template>
      </Groups>
      <InfinityUpdater
        ref="infinity"
        :action="infinityLoad"
        :active="all_members.length > count"
        :pixels_when_update="200"
        selector=" #content-container "/>
    </div>
  </v-tab-item>
</template>
<script>
import KeyValue from 'components/common/KeyValue.vue'
import Groups from 'components/common/blocks/groups/Groups.vue'
import StatusChip from 'components/common/elements/StatusChip.vue'
import EmptyBlock from 'components/common/elements/EmptyBlock.vue'
import InfinityUpdater from 'components/extends/InfinityUpdater.vue'
import VerificationStatus from 'components/tenders/VerificationStatus.vue'
import ParticipantChip from 'components/common/elements/ParticipantChip.vue'
import ContractDocuments from 'components/common/blocks/ContractDocuments.vue'
import VerificationResultBlock from 'components/supplies/VerificationResultBlock.vue'
import SubscribedToCategory from 'components/tenders/dialogs/SubscribedToCategory.vue'
import ExternalParticipantChip from 'components/tenders/tender page/ExternalParticipantChip.vue'

import moment from 'moment'
import SmarttenderApi from '@/api/smarttenderApi'
import { snackbarMixin, tenderMixin } from 'mixins'
const api = new SmarttenderApi()

export default {
  name: 'membersTab',
  mixins: [tenderMixin, snackbarMixin],
  data() {
    return {
      expanded: null,
      count: 0,
      // members: [],
      expanded_members: [],
      all_members: [],
      members_groups: [],
      // load_members: false,
      selected_members: [],
    }
  },
  props: {
    tab: Object,
    tender: Object,
    groups: Array,
    memberSearch: String,
    items: Array,
    bidding_type: Object,
    getSubscribedToCategory: Function,
    subscribedToCategory: Array,
    subscribersUsers: Array,
    in_creating: Boolean,
    load: Boolean,
    returnToStep: Function,
    contranctProps: {
      type: Object
    }
  },
  async mounted() {
    this.load_members = true
    this.$root.$on('updateSupplierVerification', this.updateSupplierVerification)

    await api.invitedParticipants(this.tender.tenderId)
      .then(res => {
        if (!res || !res.length) {
          this.members = []
          return
        }
        this.members = res?.sort((a, b) => {
          if (a.manualAdded !== b.manualAdded) {
            return a.manualAdded ? -1 : 1 // Сортировка по manualAdded (true сначала)
          }
          if (a.participationCount !== b.participationCount) {
            return b.participationCount - a.participationCount // Сортировка по participationCount (по убыванию)
          }
          return a.organizationId - b.organizationId // Сортировка по organizationId (по возрастанию)
        })
          .map((member, memberIndex) => {
            return {
              ...member,
              bidderInfo: {
                contactId: member.contactId,
                organizationId: member.organizationId,
                usreou: member.usreou,
                organizationName: member.organizationTitle,
                email: member.email,
                fullName: member.userName
              },
              uniqId: member.contactId + '_' + memberIndex
            }
          })
      })
      .catch((error) => {
        this.$log_error(error.message)
        this.$notice.error({
          title: this.$t('Tenders.Members'),
          dangerouslyUseHTMLString: true,
          message: this.$t('Common.errorLoadingData'),
          showClose: true,
          duration: 5
        })
        this.members = []
      })
    await this.getSubscribedToCategory()
    if ( this.tab.value == 'members') {
      await this.setMembersGroups()
    }
    this.load_members = false
  },
  beforeDestroy() {
    this.$root.$off('updateSupplierVerification', this.updateSupplierVerification)
  },
  computed: {
    load_members: {
      get() {
        return this.load
      },
      set(value) {
        this.$emit('update:load',value)
      }
    },
    members: {
      get() {
        return this.items
      },
      set(value) {
        this.$emit('update:items', value)
      }
    },
    per_page() {
      return Math.round(document.documentElement.clientHeight / 55) //todo
    },
  },
  methods: {
    updateSupplierVerification(props) {
      this.$_updateSupplierVerification(props)
        .then(() => {
          this.setMembersGroups()
        })
    },
    members_headers(winners) {
      let headers = [
        {text: '', value: '', width: '1%', filterable: false, sortable: false   },
        {text: this.$t('Tenders.Members'), value: 'organizationTitle', width: '33%', filterable: false },
        {text: this.$t('Tenders.EDRPOU'), value: 'organizationUsreou', sortable: false, width: '15%', filterable: false  },
        {text: this.$t('Tenders.ContactPerson'), value: 'userName', sortable: false, width: '25%', filterable: false  },
        {text: this.$t('Common.MailAddress'), value: 'email', sortable: false, width: '25%', filterable: false  },
        {text: '', value: 'empty', sortable: false, width: '1%', filterable: false },
      ]
      if (winners) {
        headers.shift()
        if ( this.tender.tenderStatus.id == 10) {
          headers.splice(-1, 0,
            { text: this.$t('Tenders.Contract'), value: 'contract', sortable: false, width: '15%'  } )
        }
      }
      if (this.tender?.tenderPublishedSchedule?.previousMailing) {
        headers.splice(-1, 0,
          { text: this.$t('Tenders.DateShipment'), value: 'previousMailingDateStr', sortable: false, width: '10%'  } )
      }
      return headers
    },
    expandRow (item) {
      if ( !item.timeSupplyBids) { return }
      let ind = this.expanded_members.findIndex(el => el.id == item.id)
      if (ind < 0) {
        this.expanded_members.push(item)
      } else {
        this.expanded_members.splice(ind, 1)
      }
    },
    async setMembersGroups() {
      await this.$_setCrmSupplierVerificationByTender(this.tender?.bids)

      this.load_members = true
      this.all_members = []
      let participants = []
      let winners = []
      let status = this.tender?.tenderStatus?.id
      let prequalification = status == 5
      let ended = status == 10
      let analis = status == 3
      const isAuction = !!this.tender?.auctionPeriod?.startDate

      this.tender.bids.forEach(bid => {
        let member = this.$_.find(this.members, item => item.contactId == bid.bidderInfo.contactId)
          || this.$_.find(this.subscribersUsers , item => item.contactId == bid.bidderInfo.contactId)
        let from_bid = this.$_.cloneDeep(bid) //this.$_.cloneDeep(this.getBids([this.$_.cloneDeep(bid)], this.tender)[0], true)

        delete from_bid.lotId
        if (!member) {
          member = {...from_bid, ...from_bid.bidderInfo}
        } else {
          member = { ...member, ...from_bid.bidderInfo }
        }
        member.timeSupplyBids = moment(Math.max(...(bid.bidDetails ?? []).map(el => Date.parse(el?.timeSupplyBids)).filter(el => el))).format('DD.MM.YYYY, HH:mm ')

        if (this.$_.some( bid.bidDetails, bid => ['ACTIVE', 'DRAFT'].includes(bid.bidStatus) )) {
          member.winner = this.$_.some( bid.bidDetails, item => prequalification ? item.prequalification?.status == 'ACTIVE' : item.selectedBid  )
          member.contract = []
          member.contractCount = {}
          member.contractStatus = {}
          if ( (ended || analis || prequalification) && member.winner ) {
            winners.push(member)
          } else {
            participants.push(member)
          }
          this.all_members.push(member)
        }
      })

      let get_member = (member) => {
        let res = this.tender?.bids?.find(bid => bid?.bidderInfo?.contactId == member.contactId)
        if (!res) {
          this.all_members.push(member)
        }
        return res
      }

      let groups = []

      if (isAuction) {
        const auctionStartDate = this.tender?.auctionPeriod?.startDate
        const memberAfterAuction = []
        const memberBeforeAuction = []
        await this.$_setCrmSupplierVerificationByTender(this.members)
        this.members.forEach((member) => {
          const existsMember = get_member(member)
          if (!existsMember) {
            if (new Date(member.dateAdd).getTime() > new Date(auctionStartDate).getTime()) {
              member.uniqId = `${member.uniqId}_after`
              memberAfterAuction.push(member)
            } else {
              member.uniqId = `${member.uniqId}_before`
              memberBeforeAuction.push(member)
            }
          }
        })

        await this.$_setCrmSupplierVerificationByTender(memberAfterAuction)
        await this.$_setCrmSupplierVerificationByTender(memberBeforeAuction)
        groups.push({
          title: this.$t('Tenders.MemberSecondSate'),
          items: memberAfterAuction,
        }, {
          title: this.$t('Tenders.MemberFirstSate'),
          items: memberBeforeAuction,
          subscribersCount: this.subscribersUsers.length,
          countGroup: {
            total: []
              .concat(memberBeforeAuction.map(i => i.contactId || i.email.toLowerCase()))
              .concat(this.subscribersUsers.map(i => i.contactId || i.email.toLowerCase()))
              .filter((v, i, a) => a.indexOf(v) === i)
              .length,
            group: [
              {
                title: this.$t('Tenders.OtherInvitedSuppliers'),
                count: memberBeforeAuction.length
              },
              {
                title: this.$t('Tenders.SubscribedToCategories'),
                count: this.subscribersUsers.length
              }]
          }
        })
      } else {
        const items = this.members?.filter(member => !get_member(member)) || []
        groups.push({
          title: this.$t('Tenders.OtherInvitedSuppliers'),
          items,
          subscribersCount: this.subscribersUsers.length,
          countGroup: {
            total: []
              .concat(items.map(i => i.contactId || i.email.toLowerCase()))
              .concat(this.subscribersUsers.map(i => i.contactId || i.email.toLowerCase()))
              .filter((v, i, a) => a.indexOf(v) === i)
              .length,
            group: [
              {
                title: this.$t('Tenders.OtherInvitedSuppliers'),
                count: items.length
              },
              {
                title: this.$t('Tenders.SubscribedToCategories'),
                count: this.subscribersUsers.length
              }]
          }
        })
      }

      await this.$_setCrmSupplierVerificationByTender(winners)
      await this.$_setCrmSupplierVerificationByTender(participants)
      if (participants?.length || winners?.length) {
        groups[0].inactive = true
        groups.unshift({
          title: this.$t('Tenders.Members'),
          items: participants || [],
          active: true,
          bidders: true
        })
        groups.unshift({
          title: prequalification ? this.$t('Tenders.prequalified.ACTIVE') : this.$t('Tenders.Winners'),
          items: winners || [],
          active: true,
          winners: true
        })
        this.$_setContracts(winners)
      }

      const subscribedToCategoryUsers = [].concat([].concat.apply([], this.subscribersUsers.map(i => i.email)))

      this.members_groups = groups
        .filter(el => {
          el.items = el.items?.filter(item => this.$_.some(Object.values(item), (value) =>
            typeof value == 'string' ? value.toLowerCase().includes(this.memberSearch?.toLowerCase()) : false))

          if (this.$can('hide', 'InvitedListSearchDialog')) {
            el.items = el.items?.filter(item => !subscribedToCategoryUsers.find(i => i === item.email)) // задача #2zngvhd
          }
          return el?.items?.length || el?.subscribersCount
        })
        .map(gr => {
          gr.items?.forEach(el => {
            el.id = el?.email + (el?.usreou || el?.organizationUsreou),
            el.previousMailingDateStr = el.previousMailingDate ? this.$d(new Date(el.previousMailingDate), 'longDate') : '-'
          })
          gr.id = gr.title
          return gr
        } )

      this.count = this.per_page
      this.expanded = this.$_.cloneDeep( this.members_groups )
      this.updGroups()
      this.load_members = false

    },
    /**
     * сортуємо весь список з групи і обрізаємо !!
     *
     * group - об2єкт групи
     * args - масив з  https://vuetifyjs.com/en/api/v-data-table/#props-custom-sort - (items: any[], sortBy: string[], sortDesc: boolean[], locale: string, customSorters?: Record<string, compareFn>) => any[]
     */
    customSortSlice(group , args) {
      let [, [sort_by], [sort_desc]] = args,
        items = this.$_.cloneDeep( group.items ),
        slice = its => its.slice(0, group.count)

      if (!sort_by) { return slice(items) }

      if ( sort_by ) {
        items = this.$_.sortBy(items, sort_by)
      }
      if ( sort_desc) {
        items = this.$_.reverse(items)
      }

      return slice(items)
    },
    updExpanded( expanded, group, push ) {
      if ( push ) {  /////expanded, group, push
        if ( this.expanded?.length ) {
          this.count += this.per_page
        } else {
          this.count = this.per_page
        }
      } else {
        this.count -= group.items.length
        if ( this.count < this.per_page) {
          this.count = this.per_page
        }
      }
      this.expanded = this.$_.cloneDeep( expanded )
      this.updGroups()
    },
    infinityLoad() {
      if (this.count < this.all_members.length) {
        this.count = Math.min(this.all_members.length, this.count + this.per_page)
        this.updGroups()
      }
    },
    updGroups() {
      let groups = this.members_groups,
        count = this.count

      groups.forEach(el => {
        /*if ( !this.expanded?.some(it => it.id == el.id ) ) {
          el.count = 1
        } else */if ( count <= 0 ) {
          el.count = 0
        } else {
          el.count = Math.min(el.items.length, count)
          count = count - el.items.length
        }
      })
    },
    filterEmptyBy(gr) {
      return gr.count || (gr?.subscribersCount && !gr?.items.length) || this.all_members.length > this.count
    },
    toggleSelectAll(group, arg) {
      return arg.value ? group.items.slice(0) : []
    }
  },
  watch: {
    selected_members(val) {
      if (val?.length) {
        this.$_openSnackbar({
          name: 'members',
          elements: val,
          tender: this.tender,
          callback: () => {
            this.selected_members  = []
            this.$forceUpdate()
            this.$_closeSnackbar()
          }
        })
      } else {
        this.$_closeSnackbar()
      }
    },
    tab(val) {
      if ( val?.value != 'members' &&  this.selected_members?.length ) {
        this.selected_members = []
      }
    },
  },
  components: {
    Groups,
    KeyValue,
    EmptyBlock,
    StatusChip,
    InfinityUpdater,
    ParticipantChip,
    ContractDocuments,
    VerificationStatus,
    SubscribedToCategory,
    ExternalParticipantChip,
    VerificationResultBlock,
  }
}
</script>
<style lang="scss">
.members-table.expanded_members {
  td {
    cursor: pointer;
  }
}
</style>
